import { SYMBOLS_FILE, EXCHANGES_FILE } from "../api-config.mjs";
import log from 'loglevel';
import axios from 'axios';

let urlCache = {};
let exchangesPromise = null;

// WARNING: This list is used as a fallback for exchanges that are not found in the exchanges.json file.
// WARNING: Add new indexes to the *END* of this list, so that older indexes remain valid
const LOCAL_EXCHANGE_CODES_FALLBACK_IN_ORDER = [
  "NYSE",
  "NASDAQ",
  "AMEX",
  "TSX",
  "TSXV",
  "OTC",
  "LSE",
  "CBOE",
  "STO",
  // "ASX",
];

const loadExchanges = ({ config }) => {
  const { endpoint = "" } = config || {};
  const exchangesJsonUrl = `${endpoint}/${EXCHANGES_FILE}`;
  if (!exchangesPromise) {
    exchangesPromise = (async () => {
      try {
        log.debug("Loading exchanges.json from:", exchangesJsonUrl);
        console.log("Loading exchanges.json from:", exchangesJsonUrl);
        const { data } = await axios.get(exchangesJsonUrl);
        const { exchanges = [] } = data || {};
        const codes = exchanges.map(e => e.code).filter(Boolean);
        return codes;
      } catch (error) {
        log.error("Failed to load exchanges.json. Using fallback list.", error);
        return LOCAL_EXCHANGE_CODES_FALLBACK_IN_ORDER;
      }
    })();
  }
  return exchangesPromise;
};

// Public helpers for consumers
export const getExchangesInOrder = async ({ config } = {}) => loadExchanges({ config });

const processAllSymbols = (allSymbols) => {
  const exchanges = {};
  for (let symbol of allSymbols) {
    if (!(symbol.exchange in exchanges)) {
      exchanges[symbol.exchange] = {};
    }
    exchanges[symbol.exchange][symbol.symbol] = symbol;
  }
  return { exchanges, allSymbols };
}

const loadSymbolsFromJson = async ({ url, config }) => {
  // Uncomment the below for testing, to introduce an arbitrary delay before loading symbols
  // const waitFor = delay => new Promise(resolve => setTimeout(resolve, delay));
  // log.debug("Waiting for 2 seconds...");
  // await waitFor(200000);
  // log.debug("Done waiting!");
  log.debug("Loading Symbols from JSON:", url);
  const response = await axios.get(url);
  // log.debug("response:", response);
  const allSymbols = response.data;
  // log.debug("allSymbols:", _allSymbol);
  return processAllSymbols(allSymbols);
};

// Synchronous helper when a list of exchange codes is already available
const indexToExchangeCodeFromList = (_exchangeIndex, exchangeCodes) => {
  const exchangeIndex = parseInt(_exchangeIndex);
  if (exchangeIndex === -1) {
    return "UNKNOWN";
  }
  return exchangeCodes[exchangeIndex];
};

const loadSymbolsFromTxt = async ({ url, config }) => {
  // Uncomment the below for testing, to introduce an arbitrary delay before loading symbols
  // const waitFor = delay => new Promise(resolve => setTimeout(resolve, delay));
  // log.debug("Waiting for 2 seconds...");
  // await waitFor(200000);
  // log.debug("Done waiting!");
  log.debug("Loading Symbols from TXT:", url);
  const response = await axios.get(url);
  const rawData = response.data;
  const exchangeCodes = await getExchangesInOrder({ config });
  const allSymbols = rawData.split("\n").map(line => {
    const [ exchangeIndexAsString, symbol, name ] = line.split("\t");
    const exchange = indexToExchangeCodeFromList(exchangeIndexAsString, exchangeCodes);
    return { exchange, symbol, name };
  }).filter(({ exchange, symbol, name }) => exchange && symbol && name);
  return processAllSymbols(allSymbols);
};

const getUrl = (config) => {
  const { endpoint = "", symbolsFile = SYMBOLS_FILE } = config;
  return `${endpoint}/${symbolsFile}`;
};

const makeSymbolRequest = async (config) => {
  const { rawDataFormat = "txt" } = config;
  const url = getUrl(config);
  log.debug("makeSymbolRequest", url);
  if (!urlCache[url]) {
    if (rawDataFormat === "json") {
      urlCache[url] = loadSymbolsFromJson({ url, config });
    } else if (rawDataFormat === "txt") {
      urlCache[url] = loadSymbolsFromTxt({ url, config });
    } else {
      throw new Error(`Symbols rawDataFormat: ${rawDataFormat} not supported`);
    }
  }
};

const getSymbol = async (exchange, symbol, config = {}) => {
  makeSymbolRequest(config);
  const url = getUrl(config);
  const { exchanges } = await urlCache[url];
  const _exchange = exchange.toUpperCase();
  const _symbol = symbol.toUpperCase();
  if (!(_exchange in exchanges) || !(_symbol in exchanges[_exchange])) {
    return null;
  } else {
    return exchanges[_exchange][_symbol];
  }
};

const isAllSymbolsLoaded = (config = {}) => {
  const url = getUrl(config);
  return url in urlCache;
};

const getAllSymbols = async (config = {}) => {
  makeSymbolRequest(config);
  const { allSymbols } = await urlCache[getUrl(config)];
  return allSymbols;
};

const getCompanyName = async (exchange, symbol) => {
  log.debug("Exchange _>", exchange, symbol);
  let _sym = await getSymbol(exchange, symbol);
  log.debug("_sym", _sym);
  const { name = null } = _sym || {};
  return name;
};

// Returns symbol count floored to the nearest 100 (e.g. 5,912 → 5,900).
// Single source of truth — avoids duplicating this business rule across components.
const getCompanyCount = async (config = {}) => {
  const symbols = await getAllSymbols(config);
  return Math.floor(symbols.length / 100) * 100;
};

export {
  getSymbol,
  getAllSymbols,
  getCompanyName,
  getCompanyCount,
  isAllSymbolsLoaded,
};
